import React from 'react';

/**
 * 리모컨 플랫 도형 아이콘 (단색 면, 그라데이션·외곽선 없음).
 * tone="teal": 밝은 배경용(시안 A 진청록 + 민트) / tone="white": 진청록 헤더 위.
 */
export type RemoteFlatIconKind = 'notice' | 'question' | 'caption' | 'bgm' | 'speaker' | 'play' | 'pause' | 'stop' | 'gear' | 'screen' | 'send' | 'chevron';
export type RemoteFlatIconTone = 'teal' | 'white';

const PALETTE: Record<RemoteFlatIconTone, { main: string; sub: string; accent: string; on: string }> = {
  teal: { main: '#0d4d44', sub: '#10504a', accent: '#4cc7ae', on: '#ffffff' },
  white: { main: '#ffffff', sub: 'rgba(255,255,255,0.72)', accent: '#4cc7ae', on: '#0d4d44' },
};

export const RemoteFlatIcon: React.FC<{
  kind: RemoteFlatIconKind;
  tone?: RemoteFlatIconTone;
  size?: number | string;
  className?: string;
  /** play/pause/stop: 바깥 원 없이 기호만 (버튼 자체가 배경일 때) */
  plain?: boolean;
}> = ({ kind, tone = 'teal', size = '1.25em', className, plain = false }) => {
  const c = PALETTE[tone];
  return (
    <svg
      className={`cp-remote-flat-icon${className ? ` ${className}` : ''}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden
      focusable="false"
    >
      {kind === 'notice' ? (
        <>
          {/* 확성기: 손잡이(사각) + 몸통(사다리꼴) + 나팔(사각) + 소리(원) */}
          <rect x="2" y="9" width="5" height="6" rx="1.2" fill={c.sub} />
          <polygon points="7,9 16,4 16,20 7,15" fill={c.main} />
          <rect x="5" y="15" width="3" height="5" rx="1" fill={c.sub} />
          <circle cx="19.5" cy="12" r="2.5" fill={c.accent} />
        </>
      ) : kind === 'question' ? (
        <>
          {/* 말풍선(원 + 꼬리 삼각) + 물음표(사각·원 조합) */}
          <circle cx="12" cy="11" r="9" fill={c.main} />
          <polygon points="6,17 4,22.5 11,19" fill={c.main} />
          <rect x="9" y="6" width="6" height="2.4" rx="1.2" fill={c.on} />
          <rect x="12.6" y="6" width="2.4" height="5.2" rx="1.2" fill={c.on} />
          <rect x="10.8" y="9.6" width="4.2" height="2.4" rx="1.2" fill={c.on} />
          <rect x="10.8" y="9.6" width="2.4" height="3.6" rx="1.2" fill={c.on} />
          <circle cx="12" cy="15.8" r="1.4" fill={c.accent} />
        </>
      ) : kind === 'caption' ? (
        <>
          {/* 마이크: 캡슐 + 받침대 + 기둥 */}
          <rect x="8" y="2.5" width="8" height="12" rx="4" fill={c.main} />
          <rect x="4.5" y="9" width="2.4" height="6" rx="1.2" fill={c.sub} />
          <rect x="17.1" y="9" width="2.4" height="6" rx="1.2" fill={c.sub} />
          <rect x="4.5" y="13.2" width="15" height="2.4" rx="1.2" fill={c.sub} />
          <rect x="10.8" y="15.4" width="2.4" height="3.2" rx="0.6" fill={c.main} />
          <rect x="7.5" y="18.4" width="9" height="2.4" rx="1.2" fill={c.accent} />
        </>
      ) : kind === 'speaker' ? (
        <>
          {/* 스피커: 사각 + 사다리꼴 + 소리(원·둥근 막대) */}
          <rect x="2.5" y="8.5" width="5" height="7" rx="1.2" fill={c.sub} />
          <polygon points="7,8.5 13.5,3.5 13.5,20.5 7,15.5" fill={c.main} />
          <circle cx="16.5" cy="12" r="1.9" fill={c.accent} />
          <rect x="19.3" y="7" width="2.4" height="10" rx="1.2" fill={c.accent} />
        </>
      ) : kind === 'play' ? (
        <>
          {/* 재생: 원 + 삼각 */}
          {plain ? (
            <polygon points="7.5,4.5 20,12 7.5,19.5" fill={c.main} />
          ) : (
            <>
              <circle cx="12" cy="12" r="10" fill={c.main} />
              <polygon points="9.5,7.5 17,12 9.5,16.5" fill={c.accent} />
            </>
          )}
        </>
      ) : kind === 'pause' ? (
        <>
          {plain ? null : <circle cx="12" cy="12" r="10" fill={c.main} />}
          <rect x={plain ? 6 : 8.5} y={plain ? 5 : 7.5} width={plain ? 4.2 : 2.6} height={plain ? 14 : 9} rx="1" fill={plain ? c.main : c.accent} />
          <rect x={plain ? 13.8 : 12.9} y={plain ? 5 : 7.5} width={plain ? 4.2 : 2.6} height={plain ? 14 : 9} rx="1" fill={plain ? c.main : c.accent} />
        </>
      ) : kind === 'stop' ? (
        <>
          {plain ? (
            <rect x="5.5" y="5.5" width="13" height="13" rx="2" fill={c.main} />
          ) : (
            <>
              <circle cx="12" cy="12" r="10" fill={c.main} />
              <rect x="8.5" y="8.5" width="7" height="7" rx="1.2" fill={c.accent} />
            </>
          )}
        </>
      ) : kind === 'gear' ? (
        <>
          {/* 톱니: 원 + 회전 사각 8개 + 중심 원 */}
          <circle cx="12" cy="12" r="7" fill={c.main} />
          {[0, 45, 90, 135, 180, 225, 270, 315].map((r) => (
            <rect key={r} x="10.5" y="1.5" width="3" height="5" rx="1" fill={c.main} transform={`rotate(${r} 12 12)`} />
          ))}
          <circle cx="12" cy="12" r="2.8" fill={c.accent} />
        </>
      ) : kind === 'screen' ? (
        <>
          <rect x="2" y="4" width="20" height="13" rx="2" fill={c.main} />
          <rect x="9" y="17" width="6" height="3" fill={c.sub} />
          <rect x="6" y="20" width="12" height="2" rx="1" fill={c.sub} />
          <circle cx="12" cy="10.5" r="2.5" fill={c.accent} />
        </>
      ) : kind === 'send' ? (
        <>
          <polygon points="3,3 22,12 3,21 6,12" fill={c.main} />
          <polygon points="6,12 22,12 3,21" fill={c.sub} />
        </>
      ) : kind === 'chevron' ? (
        <polygon points="6,9 18,9 12,16" fill={c.main} />
      ) : (
        <>
          {/* 이중 음표: 원(머리) 2 + 사각(기둥) 2 + 사각(빔) */}
          <rect x="7" y="4" width="2.6" height="13" fill={c.main} />
          <rect x="17" y="2.5" width="2.6" height="13" fill={c.main} />
          <polygon points="7,4 19.6,2.5 19.6,6.5 7,8" fill={c.sub} />
          <circle cx="6" cy="17.5" r="3.5" fill={c.main} />
          <circle cx="16" cy="16" r="3.5" fill={c.accent} />
        </>
      )}
    </svg>
  );
};

/**
 * 질문 설정 요약용 작은 상태 아이콘 (currentColor 선, off = 흐림 + 사선).
 * grade: 채점 / eye: 정답 공개 / phone: 학생 기기(개별 디바이스 대상일 때만 켬)
 */
export type RemoteOptIconKind = 'grade' | 'eye' | 'phone';
export const RemoteOptIcon: React.FC<{ kind: RemoteOptIconKind; on: boolean; label: string; disabled?: boolean }> = ({
  kind,
  on,
  label,
  disabled = false,
}) => (
  <span className={`cp-rc-opt-ico${disabled ? ' is-disabled' : on ? ' is-on' : ' is-off'}`} title={label} aria-label={label} role="img">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable="false">
      {kind === 'grade' ? (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M7.8 12.3l2.9 2.9 5.6-6" />
        </>
      ) : kind === 'eye' ? (
        <>
          <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z" />
          <circle cx="12" cy="12" r="2.8" />
        </>
      ) : (
        <>
          <rect x="6.5" y="2.5" width="11" height="19" rx="2.4" />
          <path d="M11 18.2h2" />
        </>
      )}
      {on || disabled ? null : <path d="M20 4L4 20" />}
    </svg>
  </span>
);

export default RemoteFlatIcon;
